import React, { useEffect, useState } from "react"
import { LoginFlow, UpdateLoginFlowBody, UiNodeInputAttributes } from "@ory/client"
import { kratos } from "../sdk/kratos"
import { useNavigate, useSearchParams } from "react-router-dom"

export const Login = () => {
  const [flow, setFlow] = useState<LoginFlow | null>(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const flowId = searchParams.get("flow")
    const returnTo = searchParams.get("return_to")
    const aal = searchParams.get("aal")
    const refresh = searchParams.get("refresh")
    const loginChallenge = searchParams.get("login_challenge")

    async function initLogin() {
      try {
        if (flowId) {
          const { data } = await kratos.getLoginFlow({ id: flowId })
          setFlow(data)
          return
        }

        const { data } = await kratos.createBrowserLoginFlow({
          refresh: refresh === "true",
          aal: aal ? String(aal) : undefined,
          returnTo: returnTo ? String(returnTo) : undefined,
          loginChallenge: loginChallenge ? String(loginChallenge) : undefined,
          identitySchema: "employee"
        })
        setFlow(data)
      } catch (err: any) {
        if (err.response?.status === 410) {
            window.location.href = "/login"
            return
        }
        if (err.response?.status === 400) {
             if (err.response.data.error?.id === 'session_already_available') {
                 if (process.env.REACT_APP_REDIRECT_URL) {
                     window.location.href = process.env.REACT_APP_REDIRECT_URL
                 } else {
                     navigate("/")
                 }
             }
        }
        console.error(err)
      }
    }

    initLogin()
  }, [searchParams, navigate])

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!flow) return

    const formData = new FormData(e.currentTarget)
    
    // Add the submit button's value to the form data
    // This is necessary because FormData(form) does not include the submitter
    const nativeEvent = e.nativeEvent as any; // Cast to any to access submitter safely
    if (nativeEvent.submitter) {
        const submitter = nativeEvent.submitter as HTMLButtonElement;
        if (submitter.name) {
            formData.append(submitter.name, submitter.value);
        }
    }

    const body = Object.fromEntries(formData) as unknown as UpdateLoginFlowBody

    console.log("Submitting login flow with body:", body)
    try {
      await kratos.updateLoginFlow({
        flow: flow.id,
        updateLoginFlowBody: body,
      })
      
      // If we reach here, it means success (or redirect handled by browser if not SPA-intercepted, but we are using axios/fetch via SDK)
      // Actually, the SDK follows redirects by default.
      // If the login is successful, we should check if we have a session or just navigate home.
      // For simplicity, navigate home.
      if (process.env.REACT_APP_REDIRECT_URL) {
          window.location.href = process.env.REACT_APP_REDIRECT_URL
      } else {
          navigate("/")
      }
      
    } catch (err: any) {
      if (err.response?.status === 400) {
        setFlow(err.response.data)
        return
      }
      if (err.response?.status === 422) {
          if (err.response.data.redirect_browser_to) {
              window.location.href = err.response.data.redirect_browser_to
              return
          }
      }
      console.error(err)
    }
  }

  if (!flow) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={onSubmit}>
            {flow.ui.messages?.map(msg => (
                <div key={msg.id} className={`p-4 mb-4 text-sm ${msg.type === 'error' ? 'text-red-700 bg-red-100' : 'text-green-700 bg-green-100'} rounded-lg`} role="alert">
                    {msg.text}
                </div>
            ))}

            {flow.ui.nodes.map((node, index) => {
                const { attributes } = node
                if (attributes.node_type === 'input') {
                    const { name, type, value, required, disabled, label } = attributes as UiNodeInputAttributes
                    if (type === 'hidden') {
                        return <input key={index} type={type} name={name} defaultValue={value} />
                    }
                    if (type === 'submit') {
                         return (
                            <button
                                key={index}
                                type="submit"
                                name={name}
                                value={value}
                                disabled={disabled}
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                {label?.text || "Submit"}
                            </button>
                         )
                    }
                    return (
                        <div key={index} className="mb-4">
                            {label && <label className="block text-gray-700 text-sm font-bold mb-2">{label.text}</label>}
                            <input
                                type={type}
                                name={name}
                                defaultValue={value}
                                required={required}
                                disabled={disabled}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            />
                             {node.messages.map(msg => (
                                <p key={msg.id} className="text-red-500 text-xs italic">{msg.text}</p>
                            ))}
                        </div>
                    )
                }
                return null
            })}
        </form>
      </div>
    </div>
  )
}
